import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { FileTransfer } from '@capacitor/file-transfer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Network } from '@capacitor/network';
import { useSyncExternalStore } from 'react';
import type { ReleasedMedia } from '../chat/core/ports';
import { previewPathFor } from '../chat/core/outbox/photoPreview';
import { isNative } from './platform';

/**
 * Media files kept on the phone by storage key, so media the user saw once shows offline.
 * The folder is the index: one file per key, named by the key in base64url. At open, one
 * listing of the folder fills an in-memory map; after that a lookup is a map read, and only
 * file operations reach the plugins. Components load files through the WebView's local file
 * server, so no file contents cross the bridge.
 *
 * Active only on native, after enableMediaCache(), with the FileTransfer plugin, and not on
 * Android below 11. While inactive, resolveMedia() gives back the remote URL and nothing
 * touches a plugin.
 */

const DIRECTORY = Directory.LibraryNoCloud;
const DIR = 'media-cache';
const TMP = `${DIR}/tmp`;
// Kept files: the caller marks them (thumbnails). Eviction never drops them, and they do not
// count toward its limit; logout deletes them with the rest.
const KEEP = `${DIR}/keep`;
const USED_KEY = 'media-cache-used';
const MAX_NAME = 200;
const HIGH_WATER = 200 * 1024 * 1024;
const LOW_WATER = 180 * 1024 * 1024;
const SAVE_DELAY = 5_000;
const OFFLINE_RETRY = 30_000;
const CONNECTION_ERROR = 'OS-PLUG-FLTR-0008';
// FileTransfer asks for the external storage permissions below Android 11, and the app
// declares none of them.
const STORAGE_PERMISSION_ERROR = 'OS-PLUG-FLTR-0006';
const MIN_ANDROID = 11;
const DOES_NOT_EXIST = 'OS-PLUG-FILE-0008';
const ALREADY_EXISTS = 'OS-PLUG-FILE-0010';

interface Entry {
    bytes: number;
    used: number;
    // The local URL for the WebView, and the file URI for a native viewer.
    src: string;
    uri: string;
    keep: boolean;
}

const pathOf = (name: string, keep: boolean): string => `${keep ? KEEP : DIR}/${name}`;

let active = false;
// Set by a storage permission error. The cache stays off until the app restarts.
let blocked = false;
let ready = false;
// Bumped by each wipe. Work that started in an older generation deletes what it wrote.
let generation = 0;
let listening = false;
const files = new Map<string, Entry>();
let total = 0;
// File names with a download running or a delete pending.
const inFlight = new Set<string>();
// Running downloads by file name, so a caller can wait for one.
const downloads = new Map<string, Promise<void>>();
// The app data folder's file URI, read at open. Sent photos sit under it in the outbox folder.
let dataRoot: string | null = null;
// File names of sent photos moving in from the outbox, with the local URL of their outbox file.
const moving = new Map<string, string>();
// Exact URLs the server refused with a 4xx status.
const refused = new Set<string>();
let offline = false;
let offlineTimer: ReturnType<typeof setTimeout> | undefined;
let saveTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const codeOf = (error: unknown): string | undefined => (error as { code?: string } | null)?.code;

// iOS can report a missing file or folder with another code, but its message says so.
const isMissing = (error: unknown): boolean =>
    codeOf(error) === DOES_NOT_EXIST || /not exist|no such/i.test(String((error as Error | null)?.message));

// The Android major version from the user agent. Null on other platforms.
const androidVersion = (): number | null => {
    const match = /Android (\d+)/.exec(navigator.userAgent);
    return match ? Number(match[1]) : null;
};

/** The storage key of a stored-media URL: its decoded path, without the query. Null for any other URL. */
export const storageKeyOf = (url: string, appHost: string): string | null => {
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:' || parsed.host === appHost) return null;
        return decodeURIComponent(parsed.pathname.slice(1)) || null;
    } catch {
        return null;
    }
};

// Base64url of the key's UTF-8 bytes, then the key's extension. The WebView's local server
// decodes `%` in paths, and these names hold no `%` or `/`. iOS Quick Look reads the type of a
// file from its extension. Null when the name is too long to cache.
const nameOf = (key: string): string | null => {
    let binary = '';
    for (const byte of new TextEncoder().encode(key)) binary += String.fromCharCode(byte);
    const extension = /\.([a-z0-9]{1,8})$/i.exec(key)?.[1].toLowerCase();
    const name =
        btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') + (extension ? `.${extension}` : '');
    return name.length > MAX_NAME ? null : name;
};

const nameOfUrl = (url: string): string | null => {
    const key = storageKeyOf(url, window.location.host);
    return key === null ? null : nameOf(key);
};

const tmpToken = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

const readUsed = (): Record<string, number> => {
    try {
        return JSON.parse(localStorage.getItem(USED_KEY) ?? '{}') as Record<string, number>;
    } catch (error) {
        console.error('media cache read of last use failed', error);
        return {};
    }
};

const scheduleSave = () => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
        try {
            const used = Object.fromEntries([...files].map(([name, entry]) => [name, entry.used]));
            localStorage.setItem(USED_KEY, JSON.stringify(used));
        } catch (error) {
            console.error('media cache save of last use failed', error);
        }
    }, SAVE_DELAY);
};

const setOffline = (value: boolean) => {
    clearTimeout(offlineTimer);
    if (offline === value) return;
    offline = value;
    notify();
};

// A phone can report a connection and still not reach the server, and then no network event
// comes. So a connection error turns the flag on only for a while.
const connectionFailed = () => {
    setOffline(true);
    offlineTimer = setTimeout(() => setOffline(false), OFFLINE_RETRY);
};

const listen = () => {
    if (listening) return;
    listening = true;
    Network.addListener('networkStatusChange', ({ connected }) => {
        if (active) setOffline(!connected);
    }).catch((error) => console.error('media cache network listener failed', error));
    App.addListener('resume', () => {
        if (!active) return;
        Network.getStatus()
            .then(({ connected }) => {
                if (active && connected) setOffline(false);
            })
            .catch((error) => console.error('media cache network status failed', error));
    }).catch((error) => console.error('media cache resume listener failed', error));
};

const deleteFile = (path: string) =>
    Filesystem.deleteFile({ path, directory: DIRECTORY }).catch((error) => {
        if (!isMissing(error)) console.error('media cache delete failed', path, error);
    });

// Picks its victims and takes them out of the map before the first await, so two evictions
// never pick the same file. A victim stays in flight until its file is gone.
const evict = () => {
    if (total <= HIGH_WATER) return;
    const gen = generation;
    const victims: string[] = [];
    const oldest = [...files].filter(([, entry]) => !entry.keep).sort(([, a], [, b]) => a.used - b.used);
    for (const [name, entry] of oldest) {
        if (total <= LOW_WATER) break;
        files.delete(name);
        total -= entry.bytes;
        inFlight.add(name);
        victims.push(name);
    }
    scheduleSave();
    // A row that asked while a victim was in flight got null, so it is told to ask again.
    void Promise.all(
        victims.map((name) =>
            deleteFile(pathOf(name, false)).finally(() => {
                if (gen === generation) inFlight.delete(name);
            }),
        ),
    ).then(() => {
        if (gen === generation) notify();
    });
};

const add = (name: string, bytes: number, uri: string, keep: boolean) => {
    const previous = files.get(name);
    if (previous && !previous.keep) total -= previous.bytes;
    if (!keep) total += bytes;
    files.set(name, { bytes, used: Date.now(), src: Capacitor.convertFileSrc(uri), uri, keep });
    scheduleSave();
    evict();
    notify();
};

const turnOff = (code: string) => {
    blocked = true;
    if (!active) return;
    active = false;
    console.error('media cache turned off', code);
    notify();
};

const failed = (url: string, name: string, error: unknown) => {
    const data = (error as { data?: { code?: string; httpStatus?: number } } | null)?.data;
    // The plugin's error data also holds the signed URL and the response body, so only these fields are logged.
    console.error(
        'media cache download failed',
        name,
        data ? { code: data.code, httpStatus: data.httpStatus } : (error as Error | null)?.message,
    );
    if (data?.code === STORAGE_PERMISSION_ERROR) turnOff(data.code);
    else if (data?.code === CONNECTION_ERROR) connectionFailed();
    else if (data?.httpStatus !== undefined && data.httpStatus >= 400 && data.httpStatus < 500) refused.add(url);
};

const download = async (url: string, name: string, keep: boolean): Promise<void> => {
    const gen = generation;
    inFlight.add(name);
    const tmp = `${TMP}/${tmpToken()}`;
    const target = pathOf(name, keep);
    let moved = false;
    try {
        const { uri } = await Filesystem.getUri({ path: tmp, directory: DIRECTORY });
        if (gen !== generation) return;
        // The server signs the URL already encoded. Encoding it again can change the signature.
        await FileTransfer.downloadFile({ url, path: uri, shouldEncodeUrlParams: false });
        if (gen !== generation) return;
        await Filesystem.rename({ from: tmp, to: target, directory: DIRECTORY, toDirectory: DIRECTORY });
        moved = true;
        if (gen === generation) {
            const stat = await Filesystem.stat({ path: target, directory: DIRECTORY });
            if (gen === generation) {
                add(name, stat.size, stat.uri, keep);
                return;
            }
        }
        await deleteFile(target);
    } catch (error) {
        if (gen === generation) failed(url, name, error);
    } finally {
        if (!moved) await deleteFile(tmp);
        if (gen === generation) inFlight.delete(name);
    }
};

const startDownload = (url: string, name: string, keep: boolean): Promise<void> => {
    const running = download(url, name, keep).finally(() => {
        if (downloads.get(name) === running) downloads.delete(name);
    });
    downloads.set(name, running);
    return running;
};

const open = async (gen: number): Promise<void> => {
    const started = performance.now();
    // Nothing downloads before the open ends, so whatever is in tmp/ is left from a crash.
    await Filesystem.rmdir({ path: TMP, directory: DIRECTORY, recursive: true }).catch((error) => {
        if (!isMissing(error)) throw error;
    });
    if (gen !== generation) return;
    // One after the other: two recursive creates of a missing media-cache/ race, and Android
    // reports the loser as a missing parent.
    for (const path of [TMP, KEEP]) {
        await Filesystem.mkdir({ path, directory: DIRECTORY, recursive: true }).catch((error) => {
            if (codeOf(error) !== ALREADY_EXISTS) throw error;
        });
        if (gen !== generation) return;
    }
    const [listing, kept, status, data] = await Promise.all([
        Filesystem.readdir({ path: DIR, directory: DIRECTORY }),
        Filesystem.readdir({ path: KEEP, directory: DIRECTORY }),
        Network.getStatus(),
        Filesystem.getUri({ path: '', directory: Directory.Data }),
    ]);
    if (gen !== generation) return;
    dataRoot = data.uri.replace(/\/+$/, '');
    const saved = readUsed();
    const listed = [
        ...listing.files.map((file) => ({ file, keep: false })),
        ...kept.files.map((file) => ({ file, keep: true })),
    ];
    for (const { file, keep } of listed) {
        if (file.type === 'directory') continue;
        // A hand-off can add a file while the open runs, so the total counts each name once.
        const previous = files.get(file.name);
        if (previous && !previous.keep) total -= previous.bytes;
        if (!keep) total += file.size;
        files.set(file.name, {
            bytes: file.size,
            used: saved[file.name] ?? file.mtime,
            src: Capacitor.convertFileSrc(file.uri),
            uri: file.uri,
            keep,
        });
    }
    offline = !status.connected;
    ready = true;
    console.info('media cache open', { files: files.size, ms: Math.round(performance.now() - started) });
    notify();
};

// Moves a sent photo from the outbox folder into the cache. The message payload carries the
// preview, so the outbox preview beside the photo is deleted. Until the file is in the cache,
// the photo shows from its outbox file, as it did before the send ended, so it stays on screen.
const adopt = async (path: string, name: string): Promise<void> => {
    const gen = generation;
    inFlight.add(name);
    // Outbox paths are `chat-media/<uuid>.<ext>`: nothing in them needs encoding.
    if (dataRoot !== null) moving.set(name, Capacitor.convertFileSrc(`${dataRoot}/${path}`));
    const target = `${DIR}/${name}`;
    let added = false;
    try {
        await Filesystem.deleteFile({ path: previewPathFor(path), directory: Directory.Data }).catch((error) => {
            if (!isMissing(error)) console.error('media cache preview delete failed', name, error);
        });
        if (gen !== generation) return;
        await Filesystem.rename({ from: path, to: target, directory: Directory.Data, toDirectory: DIRECTORY });
        if (gen === generation) {
            const stat = await Filesystem.stat({ path: target, directory: DIRECTORY });
            if (gen === generation) {
                add(name, stat.size, stat.uri, false);
                added = true;
                return;
            }
        }
        await deleteFile(target);
    } catch (error) {
        if (gen === generation) console.error('media cache hand-off failed', name, error);
    } finally {
        if (gen === generation) {
            inFlight.delete(name);
            moving.delete(name);
            // A row that asked while the key was moving got the outbox file, which a failed
            // move may have lost. It asks again, and the miss starts a download.
            if (!added) notify();
        }
    }
};

/** True for the chat media the cache keeps: photos and voice notes. Files open through `mediaFile`. */
export const isCachedChatMedia = (mime: string): boolean => mime.startsWith('image/') || mime.startsWith('audio/');

/**
 * Takes over the files of sent photos and voice notes from the outbox. Other media stay in the
 * outbox folder. Each key is moving before this returns, so the medium keeps showing from its
 * outbox file and downloads nothing while the move runs. A file with no URL, or one the move fails
 * on, stays in the outbox folder, which logout deletes.
 */
export const adoptMedia = (released: ReleasedMedia[]): void => {
    if (!active) return;
    for (const { path, url, mime } of released) {
        if (!isCachedChatMedia(mime)) continue;
        const name = url === null ? null : nameOfUrl(url);
        if (name !== null) void adopt(path, name);
    }
};

/** Starts the cache. The open runs in the background and notifies subscribers when it ends. */
export const enableMediaCache = (): void => {
    if (active || blocked || !isNative() || !Capacitor.isPluginAvailable('FileTransfer')) return;
    const android = androidVersion();
    if (android !== null && android < MIN_ANDROID) return;
    active = true;
    listen();
    const gen = generation;
    open(gen).catch((error) => {
        console.error('media cache open failed', error);
        if (gen !== generation) return;
        active = false;
        notify();
    });
};

/** Turns the cache off until the next enable and deletes its files and saved times. */
export const wipeMediaCache = async (): Promise<void> => {
    if (!isNative()) return;
    generation += 1;
    active = false;
    ready = false;
    files.clear();
    total = 0;
    inFlight.clear();
    downloads.clear();
    moving.clear();
    refused.clear();
    clearTimeout(offlineTimer);
    clearTimeout(saveTimer);
    offline = false;
    try {
        localStorage.removeItem(USED_KEY);
    } catch (error) {
        console.error('media cache removal of last use failed', error);
    }
    notify();
    await Filesystem.rmdir({ path: DIR, directory: DIRECTORY, recursive: true }).catch((error) => {
        if (!isMissing(error)) throw error;
    });
};

export const mediaCacheActive = (): boolean => active;

const touch = (name: string): Entry | undefined => {
    const entry = files.get(name);
    if (!entry) return undefined;
    entry.used = Date.now();
    scheduleSave();
    return entry;
};

const hit = (name: string): string | null => touch(name)?.src ?? moving.get(name) ?? null;

/** The local URL of a cached file, or null. Never downloads. */
export const lookupMedia = (url: string): string | null => {
    if (!active || !ready) return null;
    const name = nameOfUrl(url);
    return name === null ? null : hit(name);
};

/**
 * The URL to show for a stored medium. While inactive, and for a URL the cache does not keep,
 * the URL itself. On a hit, the local URL. On a miss, null, and a download starts; subscribers
 * hear when it lands. Null also while the open runs. With `keep`, the download is kept apart from
 * eviction.
 */
export const resolveMedia = (url: string, options?: { keep?: boolean }): string | null => {
    if (!active) return url;
    const name = nameOfUrl(url);
    if (name === null) return url;
    if (!ready) return null;
    const local = hit(name);
    if (local !== null) return local;
    if (!offline && !inFlight.has(name) && !refused.has(url)) void startDownload(url, name, options?.keep === true);
    return null;
};

/** A stored file on the phone, or why it is not there. */
export type MediaFile = { kind: 'file'; uri: string } | { kind: 'offline' } | { kind: 'failed' };

// Resolves when the open has ended, or when the cache is off.
const opened = (): Promise<void> =>
    new Promise((resolve) => {
        const check = () => {
            if (!ready && active) return;
            listeners.delete(check);
            resolve();
        };
        listeners.add(check);
        check();
    });

const fileOf = async (url: string, name: string): Promise<MediaFile> => {
    await opened();
    const cached = active ? touch(name) : undefined;
    if (cached) return { kind: 'file', uri: cached.uri };
    if (!active || refused.has(url)) return { kind: 'failed' };
    if (offline) return { kind: 'offline' };
    const running = downloads.get(name);
    // A delete of this file is still running.
    if (!running && inFlight.has(name)) return { kind: 'failed' };
    await (running ?? startDownload(url, name, false));
    const entry = files.get(name);
    if (entry) return { kind: 'file', uri: entry.uri };
    return offline ? { kind: 'offline' } : { kind: 'failed' };
};

/**
 * The file of a stored medium on the phone, for a native viewer. On a miss it waits for the
 * download, one per key as for resolveMedia(). Offline with no cached file it is `offline`. Null
 * while the cache is inactive and for a URL the cache does not keep: the caller then uses the URL.
 */
export const mediaFile = (url: string): Promise<MediaFile> | null => {
    if (!active) return null;
    const name = nameOfUrl(url);
    return name === null ? null : fileOf(url, name);
};

/**
 * Drops the cached file behind a local URL that failed to load, as when it was deleted outside
 * the app, so the next ask downloads it again. True when the URL was a cached file.
 */
export const reportBrokenMedia = (src: string): boolean => {
    const found = [...files].find(([, entry]) => entry.src === src);
    if (!found) return false;
    const [name, entry] = found;
    const gen = generation;
    files.delete(name);
    if (!entry.keep) total -= entry.bytes;
    inFlight.add(name);
    scheduleSave();
    notify();
    void deleteFile(pathOf(name, entry.keep)).finally(() => {
        if (gen === generation) inFlight.delete(name);
    });
    return true;
};

export const subscribeMedia = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const isMediaOffline = (): boolean => active && offline;

export const useMediaOffline = (): boolean => useSyncExternalStore(subscribeMedia, isMediaOffline);
